import React from "react";
import { FormControlLabel, Switch } from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import { useTranslation } from "react-i18next";
import { TransportOrderDetailsDto, TransportOrderState } from "../../../models/transport/transportOrder";
import { useAppDispatch, useAppSelector } from "../../../framework/redux/hooks";
import { getGeolocationPosition } from "../../../framework/common/geolocation";
import { AlertSeverity } from "../../../models/common/enums";
import { workTimeGeolocationPositionErrorDuration } from "../../worktime/workTimeTypeDrawer";
import { addErrorMsg } from "../../../framework/redux/slices/appSlice";
import { useSelectCurrentWorkShiftTimeSlot } from "../../../hooks/worktime/useSelectWorkShiftTimeSlots";

interface OrderStateToggleProps {
    order?: Partial<TransportOrderDetailsDto>;
    onStateChange: (newState: TransportOrderState, location?: GeolocationPosition | null) => void;
    setEditOrder?: (fields: Partial<TransportOrderDetailsDto>) => void;
    disabled?: boolean;
}

export const OrderStateToggle: React.FC<OrderStateToggleProps> = ({
    order,
    onStateChange,
    setEditOrder,
    disabled
}) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const employeeId = useAppSelector((state) => state.session.id);
    const usesLocationData = useAppSelector(
        ({ session }) => session.employeeGroupParameters?.usesLocationData
    );
    const current = useSelectCurrentWorkShiftTimeSlot();
    const state = order?.state;
    const isPickedUp = [TransportOrderState.PickedUp, TransportOrderState.Delivered].includes(state);
    const isDelivered = state === TransportOrderState.Delivered;

    const updateOrderState = async (newState: TransportOrderState) => {
        try {
            let location = null;
            if (usesLocationData) {
                location = await getGeolocationPosition({
                    message: t("workTime.geolocationPositionError"),
                    severity: AlertSeverity.Warning,
                    duration: workTimeGeolocationPositionErrorDuration,
                });
            }

            if (setEditOrder) {
                setEditOrder({
                    ...order,
                    employeeId,
                    modifiedEmployeeId: employeeId,
                    state: newState,
                    vehicleId: current?.vehicleId ? current.vehicleId : order.vehicleId
                });
            }
            onStateChange(newState, location);
        } catch (error) {
            dispatch(addErrorMsg({ message: t("transport.order.stateUpdateFailed") }));
        }
    };

    const handlePickupChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.checked && isDelivered) {
            void updateOrderState(TransportOrderState.OrderCreated);
        } else {
            void updateOrderState(event.target.checked ? TransportOrderState.PickedUp : TransportOrderState.OrderCreated);
        }
    };

    const handleDeliveryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        void updateOrderState(event.target.checked ? TransportOrderState.Delivered : TransportOrderState.PickedUp);
    };

    return (
        <Grid2 container spacing={2}>
            <Grid2 xs={6} display="flex" justifyContent="center">
                <FormControlLabel
                    control={
                        <Switch
                            checked={isPickedUp}
                            onChange={handlePickupChange}
                            color="primary"
                            disabled={disabled}
                        />
                    }
                    label={t("transport.order.pickedUp")}
                    sx={{
                        "& .MuiFormControlLabel-label": {
                            color: "primary.dark",
                            fontWeight: "bold",
                            fontSize: "0.8em"
                        }
                    }}
                />
            </Grid2>
            <Grid2 xs={6} display="flex" justifyContent="center">
                <FormControlLabel
                    control={
                        <Switch
                            checked={isDelivered}
                            onChange={handleDeliveryChange}
                            color="secondary"
                            disabled={disabled}
                        />
                    }
                    label={t("transport.order.delivered")}
                    sx={{
                        "& .MuiFormControlLabel-label": {
                            color: "primary.dark",
                            fontWeight: "bold",
                            fontSize: "0.8em"
                        }
                    }}
                />
            </Grid2>
        </Grid2>
    );
};